import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface UsageSummary {
  ctm_mode: 'byo' | 'agency';
  currency: string;
  period_start: string;
  period_end: string;
  days_in_month: number;
  days_elapsed: number;
  numbers: number;
  calls: number;
  call_minutes: number;
  texts: number;
  total_cents: number;
  projected_cents: number;
  threshold_cents: number;
  alert_crossed: boolean;
  credit_cents: number;
  number_month_cents: number | null;
  call_minute_cents: number | null;
  text_message_cents: number | null;
}

export const USAGE_SUMMARY_KEY = ['usage-summary'] as const;

/** This month's call-tracking meter for the current tenant (admins only). */
export function useUsageSummary(enabled = true) {
  const queryClient = useQueryClient();

  const query = useQuery<UsageSummary | null>({
    queryKey: USAGE_SUMMARY_KEY,
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_usage_summary');
      if (error) throw error;
      const row = (data ?? [])[0] as Partial<UsageSummary> | undefined;
      if (!row) return null;
      return {
        ctm_mode: (row.ctm_mode as UsageSummary['ctm_mode']) ?? 'byo',
        currency: row.currency ?? 'AUD',
        period_start: String(row.period_start ?? ''),
        period_end: String(row.period_end ?? ''),
        days_in_month: Number(row.days_in_month ?? 30),
        days_elapsed: Number(row.days_elapsed ?? 1),
        numbers: Number(row.numbers ?? 0),
        calls: Number(row.calls ?? 0),
        call_minutes: Number(row.call_minutes ?? 0),
        texts: Number(row.texts ?? 0),
        total_cents: Number(row.total_cents ?? 0),
        projected_cents: Number(row.projected_cents ?? 0),
        threshold_cents: Number(row.threshold_cents ?? 5000),
        alert_crossed: row.alert_crossed === true,
        credit_cents: Number(row.credit_cents ?? 0),
        number_month_cents: row.number_month_cents == null ? null : Number(row.number_month_cents),
        call_minute_cents: row.call_minute_cents == null ? null : Number(row.call_minute_cents),
        text_message_cents: row.text_message_cents == null ? null : Number(row.text_message_cents),
      };
    },
  });

  const setThreshold = useMutation({
    mutationFn: async (cents: number) => {
      const { error } = await supabase.rpc('set_usage_alert_threshold', { p_cents: Math.round(cents) });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USAGE_SUMMARY_KEY });
    },
  });

  return {
    summary: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    setThreshold: setThreshold.mutateAsync,
    isSavingThreshold: setThreshold.isPending,
  };
}
